import Calendar from '@atlaskit/calendar';
import { Checkbox } from '@atlaskit/checkbox';
import { DatePicker, DateTimePicker } from '@atlaskit/datetime-picker';
import { token } from '@atlaskit/tokens';
import { useDatabaseContent } from '@databases/database-state';
import FieldDecorator from '@databases/form/FieldDecorator';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import { TimeFormat } from '@databases/model/field';
import { isEmbed } from '@databases/modes';
import { MenuPortalContext } from '@databases/react/menuPortal';
import { getService } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import {
  DATE_VALUE_TODAY,
  parseDate,
  getLocalizedDatePlaceholder,
  normalizeDatetimeString
} from '@databases/utils/database/field-types/date';
import React, {
  type FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { type EditComponentProps } from './types.ts';

const SELECT_HEIGHT = 40;
const BULK_EDIT_DATETIME_WIDTH = 280;
const DATE_PICKER_MENU_OFFSET = 4;

type MenuPortalStyles = {
  menuPortal: (
    base: React.CSSProperties,
    props: { offset: number }
  ) => React.CSSProperties;
};

type DatePickerSelectRef = {
  blur: () => void;
  select: { controlRef: HTMLElement | null } | null;
};

const BULK_EDIT_DATE_CALENDAR_TEST_ID = 'bulk-edit-date-calendar';

const bulkEditDateCalendarContainerStyles: React.CSSProperties = {
  backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
  borderRadius: '3px',
  boxShadow: token(
    'elevation.shadow.overlay',
    '0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)'
  ),
  insetInlineStart: '50%',
  marginBlockEnd: token('space.100', '8px'),
  position: 'relative',
  transform: 'translateX(-50%)',
  overflow: 'hidden',
  width: 'max-content'
};

const getDateAndZoneForDateTime = (value: string | undefined) => {
  const parsedValue = parseDate(value, true);
  const match = parsedValue?.match(/^(\d{4}-\d{2}-\d{2})(?:T\d{2}:\d{2}(.*))?$/);

  return match
    ? {
        date: match[1],
        zone: match[2] ?? ''
      }
    : undefined;
};

const DateValueEdit: FunctionComponent<EditComponentProps<DatabaseFieldType.DATE>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  isDefaultValue,
  entryId,
  isBulkEdit,
  ariaLabel
}) => {
  const { formatMessage } = useIntl();
  const menuPortalTarget = useContext(MenuPortalContext);

  const locale = useLocale();
  const selectionsActions = getService('SelectionsActions');

  const dateFieldSettingsEnabled = getExperimentValue('dateFieldSettings', 'enabled', {
    fireExperimentExposure: true
  });
  const timeFormat = dateFieldSettingsEnabled ? field.settings.timeFormat : undefined;
  const includeTime = Boolean(timeFormat);
  const is12Hour = timeFormat === TimeFormat.TWELVE_HOUR;

  const effectiveAriaLabel =
    ariaLabel ??
    (includeTime
      ? formatMessage(messages['field-settings.date-and-time-picker-label'])
      : undefined);

  const datePickerSelectRef = useRef<DatePickerSelectRef>(null);

  const handleChange = useCallback(
    (val: string | undefined) => {
      let normalizedValue = val || undefined;
      if (normalizedValue && includeTime && normalizedValue.includes('T')) {
        normalizedValue = normalizeDatetimeString(normalizedValue);
      }

      onChange(normalizedValue, {
        type: DatabaseFieldType.DATE,
        value: normalizedValue
      });
      if (!isBulkEdit) {
        datePickerSelectRef.current?.blur();
        selectionsActions.deselectAll();
      }
    },
    [selectionsActions, onChange, includeTime, isBulkEdit]
  );

  const handleToggleToday = useCallback(() => {
    if (value === DATE_VALUE_TODAY) {
      handleChange(parseDate(value, includeTime));
    } else {
      handleChange(DATE_VALUE_TODAY);
    }
  }, [handleChange, value, includeTime]);

  const [isOpen, setIsOpen] = useState(false);

  const { entryId: isEntryEmbed } = useDatabaseContent();
  const paddingId = 'date-picker-padding';
  const removePadding = useCallback(() => {
    const padding = document.getElementById(paddingId);
    if (padding && padding.parentNode) {
      padding.parentNode.removeChild(padding);
    }
  }, []);

  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEmbed() && pickerRef.current) {
      // the date picker gets cut off if:
      // 1. there is not enough space above the cell, so it wants to open below
      //    the cell
      // 2. there is not enough space below the cell to fit the date picker
      const { bottom: pickerBottomPosition } = pickerRef.current.getBoundingClientRect();
      const observer = document.getElementById('resizeObserver');
      const observerHeight = (observer?.offsetHeight || 0) + 30;
      const datePickerHeight = 300;

      const canFitAbove = pickerBottomPosition > datePickerHeight;

      // there is 40px of additional space between the date picker and the cell when
      // the date picker is rendered below, design system has a ticket to fix this
      // adding 10px for extra padding
      const requiredSpaceBelow = datePickerHeight + 50;
      const availableSpaceBelow = observerHeight - pickerBottomPosition;
      const canFitBelow = availableSpaceBelow > requiredSpaceBelow;

      const additionalHeight = requiredSpaceBelow - availableSpaceBelow;

      if (!isOpen) {
        removePadding();
      } else if (!canFitAbove && !canFitBelow) {
        const padding = document.createElement('div');
        padding.style.height = additionalHeight + 'px';
        padding.id = paddingId;
        observer?.appendChild(padding);
      }
    }
  }, [entryId, isDefaultValue, isEntryEmbed, isOpen, removePadding]);

  const handleFocus = useCallback(() => {
    // wrapping in setTimeout to prevent the date picker to appear in the wrong position first
    // and after a re-render in the correct position
    if (!isOpen) {
      setTimeout(() => {
        setIsOpen(true);
      }, 0);
      onFocus?.();
    }
  }, [onFocus, isOpen]);

  const handleBlur = useCallback(() => {
    setIsOpen(false);
    onBlur?.();
  }, [onBlur]);

  const [isBulkEditDateCalendarOpen, setIsBulkEditDateCalendarOpen] = useState(false);
  const lastBulkEditDateAndZoneRef =
    useRef<ReturnType<typeof getDateAndZoneForDateTime>>();

  useEffect(() => {
    const dateAndZone = getDateAndZoneForDateTime(value);

    if (dateAndZone) {
      lastBulkEditDateAndZoneRef.current = dateAndZone;
    }
  }, [value]);

  const handleOpenBulkEditDateCalendar = useCallback(() => {
    if (isBulkEdit && includeTime) {
      setIsBulkEditDateCalendarOpen(true);
    }
  }, [includeTime, isBulkEdit]);

  const handleBulkEditDateCalendarKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!isBulkEdit || !includeTime) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsBulkEditDateCalendarOpen(true);
      }
    },
    [includeTime, isBulkEdit]
  );

  const handleBulkEditDateSelect = useCallback(
    (date: { iso: string }) => {
      const parsedValue = parseDate(value, includeTime);
      const existingTimeAndZone = parsedValue?.match(/^\d{4}-\d{2}-\d{2}(.*)$/)?.[1];

      handleChange(`${date.iso}${existingTimeAndZone ?? ''}`);
      setIsBulkEditDateCalendarOpen(false);
    },
    [handleChange, includeTime, value]
  );

  const handleBulkEditTimeChange = useCallback(
    (timeValue: string) => {
      const dateAndZone =
        getDateAndZoneForDateTime(value) ?? lastBulkEditDateAndZoneRef.current;

      if (!dateAndZone) {
        return;
      }

      handleChange(
        timeValue ? `${dateAndZone.date}T${timeValue}${dateAndZone.zone}` : undefined
      );
    },
    [handleChange, value]
  );

  useEffect(() => {
    // to cover the case where the component is unmounted, ex. date picker is
    // open in a filter and the filter button is clicked
    return () => {
      removePadding();
    };
  }, [removePadding]);

  const calendarRef = useRef<HTMLDivElement>(null);

  const [calendarSize, setCalendarSize] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const calendarElement = calendarRef.current;
    if (!calendarElement) {
      return;
    }

    const handleResize = () => {
      setCalendarSize({
        height: calendarElement.clientHeight,
        width: calendarElement.clientWidth
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(calendarElement);

    return () => {
      resizeObserver.unobserve(calendarElement);
    };
  }, []);

  const getDatePickerControlHeight = useCallback(
    () =>
      datePickerSelectRef.current?.select?.controlRef?.getBoundingClientRect().height ??
      SELECT_HEIGHT,
    []
  );

  const datePickerMenuPortalStyles = useMemo<MenuPortalStyles>(
    () => ({
      menuPortal: (base, { offset }) => {
        return {
          ...base,
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          top: offset - getDatePickerControlHeight() + DATE_PICKER_MENU_OFFSET
        };
      }
    }),
    [getDatePickerControlHeight]
  );

  const datePickerSelectProps = useMemo(
    () => ({
      ref: datePickerSelectRef,
      menuPortalTarget,
      menuPlacement: 'bottom' as const,
      styles: datePickerMenuPortalStyles
    }),
    [datePickerMenuPortalStyles, menuPortalTarget]
  );

  const dateTimeSelectProps = useMemo(
    () => ({
      ref: datePickerSelectRef
    }),
    []
  );
  const dateTimeTimeSelectProps = useMemo(
    () => (isBulkEdit ? { menuPortalTarget, styles: datePickerMenuPortalStyles } : {}),
    [datePickerMenuPortalStyles, isBulkEdit, menuPortalTarget]
  );
  const bulkEditDateTimeDateSelectProps = useMemo(
    () => ({
      onKeyDown: handleBulkEditDateCalendarKeyDown
    }),
    [handleBulkEditDateCalendarKeyDown]
  );

  const timePickerProps = useMemo(
    () => ({
      timeFormat: is12Hour ? 'h:mm a' : 'HH:mm',
      onChange: isBulkEdit ? handleBulkEditTimeChange : undefined,
      onFocus: isBulkEdit
        ? () => {
            setIsBulkEditDateCalendarOpen(false);
            handleFocus();
          }
        : undefined
    }),
    [handleBulkEditTimeChange, handleFocus, is12Hour, isBulkEdit]
  );

  const datePickerProps = useMemo(
    () => ({
      placeholder: getLocalizedDatePlaceholder(locale),
      isOpen: isBulkEdit ? false : undefined,
      innerProps:
        isBulkEdit && includeTime
          ? {
              onMouseDown: handleOpenBulkEditDateCalendar
            }
          : undefined
    }),
    [handleOpenBulkEditDateCalendar, includeTime, isBulkEdit, locale]
  );

  const selectedCalendarDate = parseDate(value, includeTime)?.slice(0, 10);
  const selectedCalendarDateParts = selectedCalendarDate?.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  const picker = (
    <div
      ref={pickerRef}
      data-database-cell-editor-focus-ring={isBulkEdit ? undefined : ''}
      style={
        isBulkEdit
          ? includeTime
            ? {
                width: BULK_EDIT_DATETIME_WIDTH
              }
            : {
                width: calendarSize.width,
                height: calendarSize.height + SELECT_HEIGHT
              }
          : {}
      }
    >
      {includeTime ? (
        <>
          {isBulkEdit && isBulkEditDateCalendarOpen && (
            <div
              data-testid={BULK_EDIT_DATE_CALENDAR_TEST_ID}
              style={bulkEditDateCalendarContainerStyles}
            >
              <Calendar
                locale={locale}
                defaultDay={
                  selectedCalendarDateParts ? Number(selectedCalendarDateParts[3]) : 0
                }
                defaultMonth={
                  selectedCalendarDateParts ? Number(selectedCalendarDateParts[2]) : 0
                }
                defaultSelected={[selectedCalendarDate || '']}
                defaultYear={
                  selectedCalendarDateParts ? Number(selectedCalendarDateParts[1]) : 0
                }
                onSelect={handleBulkEditDateSelect}
              />
            </div>
          )}
          <DateTimePicker
            value={parseDate(value, includeTime) ?? ''}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- inline cell edit relies on focus for discoverability
            autoFocus={autoFocus}
            locale={locale}
            isDisabled={isDefaultValue && value === DATE_VALUE_TODAY}
            datePickerProps={{
              ...datePickerProps,
              selectProps: isBulkEdit
                ? bulkEditDateTimeDateSelectProps
                : dateTimeSelectProps
            }}
            timePickerProps={{
              ...timePickerProps,
              selectProps: dateTimeTimeSelectProps
            }}
            appearance="none"
            aria-label={effectiveAriaLabel}
          />
        </>
      ) : (
        <>
          <DatePicker
            value={parseDate(value) ?? ''}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            isOpen={isBulkEdit ? false : isOpen}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- inline cell edit relies on focus for discoverability
            autoFocus={autoFocus}
            placeholder={getLocalizedDatePlaceholder(locale)}
            locale={locale}
            isDisabled={isDefaultValue && value === DATE_VALUE_TODAY}
            selectProps={datePickerSelectProps}
            appearance="none"
            aria-label={effectiveAriaLabel}
          />
          {isBulkEdit && (
            <Calendar
              ref={calendarRef}
              locale={locale}
              defaultSelected={[parseDate(value) || '']}
              onSelect={(date) => handleChange(date.iso)}
            />
          )}
        </>
      )}
    </div>
  );

  if (isDefaultValue) {
    return (
      <>
        {picker}
        <FieldDecorator isCompact>
          <Checkbox
            isChecked={value === DATE_VALUE_TODAY}
            onChange={handleToggleToday}
            onFocus={onFocus}
            onBlur={onBlur}
            label={formatMessage(
              messages[
                includeTime
                  ? 'field-settings.date-default-value-with-time'
                  : 'field-settings.date-default-value'
              ]
            )}
          />
        </FieldDecorator>
      </>
    );
  } else {
    return picker;
  }
};

export default DateValueEdit;
